import { Injectable, inject } from '@angular/core';
import { interval, Subscription } from 'rxjs';
import { AuthService } from './auth.service';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly auth = inject(AuthService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);

  private watcher?: Subscription;
  private warningShown = false;

  start(): void {
    this.stop();

    this.watcher = interval(5000).subscribe(() => {
      const expiresAt = localStorage.getItem('expires_at');
      if (!expiresAt) return;

      const expiration = new Date(expiresAt).getTime();
      const now = Date.now();
      const diff = expiration - now;

      // Aviso 1 minuto antes
      if (diff <= 80000 && diff > 0 && !this.warningShown) {
        this.warningShown = true;
        this.showExpirationWarning();
      }

      // Sessão expirada
      if (diff <= 0) {
        this.onSessionExpired();
      }
    });
  }

  stop(): void {
    this.watcher?.unsubscribe();
    this.warningShown = false;
  }

  private showExpirationWarning(): void {
    this.confirmationService.confirm({
      header: 'Sessão a expirar',
      message:
        'A sua sessão vai expirar em menos de 1 minuto. Deseja continuar?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Continuar sessão',
      rejectLabel: 'Sair',
      accept: () => this.refreshSession(),
      reject: () => this.forceLogout(),
    });
  }

  private onSessionExpired(): void {
    this.stop();

    this.messageService.add({
      severity: 'warn',
      summary: 'Sessão expirada',
      detail: 'A tentar renovar a sessão...',
      life: 5000,
    });

    this.refreshSession();
  }

  private refreshSession(): void {
    this.auth.refresh().subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sessão renovada',
          detail: 'Pode continuar a usar a aplicação',
          life: 5000,
        });
        location.reload();
      },
      error: () => this.forceLogout(),
    });
  }

  private forceLogout(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Sessão terminada',
      detail: 'Faça login novamente',
      life: 5000,
    });

    const accessToken: string | null = this.auth.getAccessToken();

    if (!accessToken) {
      this.auth.clearSession();
      return;
    }

    this.auth.logout();
  }
}
